import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { preferencesAPI } from '@/api/preferences';
import api from '@/services/api';
import { soundService } from '@/services/soundService';
import { getAuthToken, getPortalType, isAuthenticated, getActivePortal } from '@/utils/auth';
import { updateSystemBarsTheme } from '@/utils/systemBars';

const PreferencesContext = createContext(null);

export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

const updateBootstrapCache = (updatedPrefs) => {
  const BOOTSTRAP_DATA_KEY = 'bootstrap_init_data_v2';
  try {
    const cachedRaw = localStorage.getItem(BOOTSTRAP_DATA_KEY);
    if (cachedRaw) {
      const cached = JSON.parse(cachedRaw);
      if (cached && cached.data) {
        cached.data.preferences = {
          ...(cached.data.preferences || {}),
          ...updatedPrefs
        };
        localStorage.setItem(BOOTSTRAP_DATA_KEY, JSON.stringify(cached));
      }
    }
  } catch (err) {
    console.error('Error updating bootstrap cache:', err);
    localStorage.removeItem(BOOTSTRAP_DATA_KEY);
  }
};

export const PreferencesProvider = ({ children, initialData = null }) => {
  const [preferences, setPreferences] = useState(() => {
    if (initialData) return initialData;
    // Initial preferences from localStorage
    const savedSound = localStorage.getItem('budlee_sound_effects');
    const savedConsent = localStorage.getItem('budlee_ai_consent');
    const savedPush = localStorage.getItem('budlee_daily_push');
    const savedEmail = localStorage.getItem('budlee_daily_email');
    const savedParent = localStorage.getItem('budlee_parent_email');
    const savedTimezone = localStorage.getItem('budlee_timezone');
    return {
      sound_effects_enabled: savedSound === null ? true : savedSound === 'true',
      ai_data_consent: savedConsent === 'true',
      daily_push_enabled: savedPush === null ? true : savedPush === 'true',
      daily_email_enabled: savedEmail === null ? true : savedEmail === 'true',
      parent_email_enabled: savedParent === null ? false : savedParent === 'true',
      timezone: savedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata',
      favorite_tutors: (() => {
        try {
          return JSON.parse(localStorage.getItem('budlee_favorite_tutors') || '[]');
        } catch (_) {
          return [];
        }
      })(),
    };
  });
  const [config, setConfig] = useState(() => {
    let cachedFeatures = {};
    try {
      cachedFeatures = JSON.parse(localStorage.getItem('userportal_config') || '{}');
    } catch (_) {}
    if (initialData?.config) return { ...cachedFeatures, ...initialData.config };
    return {
      max_session_limit: 2,
      budlee_tab_enabled: true,
      voice_assistant_enabled: false,
      arabic_language_support_enabled: false,
      v2_learn_enabled_grades: [],
      v2_learn_enabled_scopes: [],
      v2_learn_all_enabled: true,
      v2_learn_email_allowed: true,
      ...cachedFeatures,
    };
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState(() => {
    if (initialData?.theme) return initialData.theme;
    if (initialData?.preferences?.theme) return initialData.preferences.theme;
    // Initial theme detection
    const savedTheme = localStorage.getItem('budlee_theme');
    if (savedTheme) return savedTheme;
    return 'light'; // Default to light mode for new users
  });

  // Initial hydration if data is provided later
  useEffect(() => {
    if (initialData) {
      if (initialData.preferences) setPreferences(initialData.preferences);
      else if (initialData.id) setPreferences(initialData); // Some responses send raw prefs
      
      if (initialData.theme) setTheme(initialData.theme);
      else if (initialData.preferences?.theme) setTheme(initialData.preferences.theme);

      if (initialData.config) setConfig(initialData.config);
    }
  }, [initialData]);

  // Apply theme class to document root and save to localStorage
  useEffect(() => {
    const portal = getPortalType(window.location.pathname);
    
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Dynamic PWA Theme Color
    // Student Dark: #0d1f1a, Student Light: #ffffff
    // Teacher Dark: #0d1f1a, Teacher Light: #f6f3ef (Teacher card bg)
    const themeColor = theme === 'dark' 
      ? '#0d1f1a' 
      : (portal === 'teacher' ? '#f6f3ef' : '#ffffff');

    // Update meta theme-color (Clearing existing ones to remove media query fallbacks once app loads)
    document.querySelectorAll('meta[name="theme-color"]').forEach(el => el.remove());
    const metaThemeColor = document.createElement('meta');
    metaThemeColor.name = 'theme-color';
    metaThemeColor.setAttribute('content', themeColor);
    document.head.appendChild(metaThemeColor);

    // Update MS Tile color
    let msTileColor = document.querySelector('meta[name="msapplication-TileColor"]');
    if (msTileColor) {
      msTileColor.setAttribute('content', themeColor);
    }

    // Sync system bars (native Android nav & status bars + iOS status bar)
    updateSystemBarsTheme(theme === 'dark', themeColor);

    localStorage.setItem('budlee_theme', theme);
  }, [theme, window.location.pathname]);

  // Sync sound effects to localStorage and soundService
  useEffect(() => {
    const enabled = preferences?.sound_effects_enabled ?? true;
    localStorage.setItem('budlee_sound_effects', enabled.toString());
    soundService.setSoundEnabled(enabled);
  }, [preferences?.sound_effects_enabled]);

  /**
   * Fetch user preferences from backend
   */
  const fetchPreferences = useCallback(async () => {
    const portal = getActivePortal();
    const isActuallyGuest = localStorage.getItem('is_guest_session') === 'true';
    const isAuthed = isAuthenticated(portal);
    
    if (!isAuthed || isActuallyGuest) {
      // Guest user or unauthenticated
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await preferencesAPI.getPreferences();
      
      // Update local storage with sync from DB
      if (data.theme) {
        setTheme(data.theme);
        localStorage.setItem('budlee_theme', data.theme);
      }
      
      if ('sound_effects_enabled' in data) {
        localStorage.setItem('budlee_sound_effects', data.sound_effects_enabled.toString());
      }

      if ('ai_data_consent' in data) {
        localStorage.setItem('budlee_ai_consent', data.ai_data_consent.toString());
      }

      if ('daily_push_enabled' in data) {
        localStorage.setItem('budlee_daily_push', data.daily_push_enabled.toString());
      }

      if ('daily_email_enabled' in data) {
        localStorage.setItem('budlee_daily_email', data.daily_email_enabled.toString());
      }

      if ('parent_email_enabled' in data) {
        localStorage.setItem('budlee_parent_email', data.parent_email_enabled.toString());
      }

      if (data.timezone) {
        localStorage.setItem('budlee_timezone', data.timezone);
      }

      setPreferences(data);

      // Auto-populate/update timezone if missing, or if it differs from the browser's current timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
      
      if (!data.timezone || data.timezone !== browserTimezone) {
        // Run asynchronously to avoid blocking the fetch return
        setTimeout(() => {
          updatePreferences({ timezone: browserTimezone }).catch(() => {});
        }, 0);
      }

      // Notify other tabs
      localStorage.setItem('preferences_data_updated', JSON.stringify(data));
      updateBootstrapCache(data);
      return data;
    } catch (err) {
      console.error('[PreferencesContext] Error fetching preferences:', err);
      // Don't clear local preferences on error, just report it
      setError(err.message || 'Failed to fetch preferences');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPortalConfig = useCallback(async () => {
    try {
      const response = await api.get('/userportal/config/');
      const features = response?.data?.features || {};
      try {
        localStorage.setItem('userportal_config', JSON.stringify(features));
        window.dispatchEvent(new CustomEvent('userportal_config_updated', { detail: features }));
      } catch (_) {}
      setConfig(prev => ({
        ...prev,
        ...features,
      }));
      return features;
    } catch (err) {
      console.error('[PreferencesContext] Error fetching portal config:', err);
      return null;
    }
  }, []);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (updates) => {
    // Always update local state first for immediate UI response
    if ('theme' in updates) {
      setTheme(updates.theme);
      localStorage.setItem('budlee_theme', updates.theme);
    }
    
    if ('sound_effects_enabled' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_sound_effects', updates.sound_effects_enabled.toString());
    } else if ('ai_data_consent' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_ai_consent', updates.ai_data_consent.toString());
    } else if ('daily_push_enabled' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_daily_push', updates.daily_push_enabled.toString());
    } else if ('daily_email_enabled' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_daily_email', updates.daily_email_enabled.toString());
    } else if ('parent_email_enabled' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_parent_email', updates.parent_email_enabled.toString());
    } else if ('timezone' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      localStorage.setItem('budlee_timezone', updates.timezone);
    } else if ('favorite_tutors' in updates) {
      setPreferences(prev => ({ ...prev, ...updates }));
      try {
        localStorage.setItem('budlee_favorite_tutors', JSON.stringify(updates.favorite_tutors));
      } catch (_) {}
    } else {
      setPreferences(prev => ({ ...prev, ...updates }));
    }

    const portal = getPortalType(window.location.pathname || '');
    const isAuthed = isAuthenticated(portal);

    if (!isAuthed) {
      // Guest user or unauthenticated - only local update needed
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updated = await preferencesAPI.updatePreferences(updates);
      setPreferences(updated);
      updateBootstrapCache(updated);
      return updated;
    } catch (err) {
      console.error('[PreferencesContext] Error updating preferences:', err);
      setError(err.message || 'Failed to update preferences');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Toggle between light and dark theme
   */
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    updatePreferences({ theme: newTheme });
  }, [theme, updatePreferences]);

  /**
   * Play celebration sound (if enabled)
   */
  const playCelebrationSound = useCallback(() => {
    if (preferences?.sound_effects_enabled !== false) {
      soundService.playCelebrationSound();
    }
  }, [preferences]);

  // Track last config fetch timestamp to prevent focus/visibility thrashing
  let lastConfigFetchTimestamp = 0;

  // Fetch preferences on mount and when token changes
  useEffect(() => {
    // Optimization: If initialData was provided via Bootstrap, skip the initial fetch
    if (initialData) {
      console.log('[PreferencesContext] Skipping initial fetch (hydrated from Bootstrap)');
    } else {
      fetchPreferences();
    }
    
    // Also listen for login events
    const handleLogin = () => fetchPreferences();
    const handleFocusOrVisible = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      // Throttle window focus and visibility re-fetches to at most once per 5 minutes
      if (now - lastConfigFetchTimestamp < 300000) return;
      lastConfigFetchTimestamp = now;
      fetchPortalConfig();
    };

    lastConfigFetchTimestamp = Date.now();
    fetchPortalConfig();
    window.addEventListener('login_success', handleLogin);
    window.addEventListener('focus', handleFocusOrVisible);
    document.addEventListener('visibilitychange', handleFocusOrVisible);
    return () => {
      window.removeEventListener('login_success', handleLogin);
      window.removeEventListener('focus', handleFocusOrVisible);
      document.removeEventListener('visibilitychange', handleFocusOrVisible);
    };
  }, [fetchPortalConfig, fetchPreferences, initialData]);
  
  // Tab synchronization for preferences
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferences_data_updated') {
        try {
          const data = JSON.parse(e.newValue);
          if (data) {
            console.log('[PreferencesContext] Syncing preferences from another tab');
            setPreferences(data);
            if (data.theme) setTheme(data.theme);
          }
        } catch (_) {}
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const toggleFavoriteTutor = useCallback(async (tutorId) => {
    if (!tutorId) return;
    const currentList = Array.isArray(preferences?.favorite_tutors) ? preferences.favorite_tutors : [];
    const isFav = currentList.includes(tutorId) || currentList.includes(Number(tutorId)) || currentList.includes(String(tutorId));
    const normalizedTarget = Number(tutorId) || tutorId;
    const updated = isFav
      ? currentList.filter(id => id !== tutorId && id !== Number(tutorId) && id !== String(tutorId))
      : [...currentList, normalizedTarget];

    try {
      await updatePreferences({ favorite_tutors: updated });
    } catch (err) {
      console.error('Failed to update favorite tutor', err);
    }
    return !isFav;
  }, [preferences?.favorite_tutors, updatePreferences]);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        config,
        loading,
        error,
        theme,
        toggleTheme,
        fetchPreferences,
        updatePreferences,
        toggleFavoriteTutor,
        playCelebrationSound,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};
